import {getAcceptedKycDocumentTypes} from '@sollapay/domain';
import {DocumentContext, DocumentEntityType, PartyType} from '@sollapay/enums';
import {useWatch} from 'react-hook-form';

import {useFormShell} from '@/hooks';

import type {CreateBuyerFormValues} from '../validation';
import type {AiSuggestion, ScopeAiShell} from '@/features/ai-extraction';
import type {AiDocumentScope} from '@/hooks';
import type {DocumentType} from '@sollapay/enums';
import type {Control, FieldArrayWithId, UseFormReturn} from 'react-hook-form';

// Row scopes carry their own KYC types, so a form with no shared scope needs no default types.
const NO_SHARED_DOCUMENT_TYPES: DocumentType[] = [];

export interface BuyerFormSharedAiScope {
  scope: AiDocumentScope;
  documentContext: DocumentContext;
  documentTypes: readonly DocumentType[];
  resolveField: (targetField: string) => string | null;
}

interface UseBuyerFormAiParams {
  form: UseFormReturn<CreateBuyerFormValues>;
  buyerFields: FieldArrayWithId<CreateBuyerFormValues, 'buyers'>[];
  trustAccountId: string;
  isSaving: boolean;
  isSaveError: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
  sharedScope?: BuyerFormSharedAiScope;
}

/** AI state per scope — a buyer row's scope id is its `buyerKey`. */
export type BuyerRowAi = ScopeAiShell;

/** `isProcessing`, `suggestionsCount` and `handleFillManually` cover the form-wide (contract) scope only; each buyer's own uploads are in `rowAi`. */
export interface BuyerFormAi {
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  suggestionsCount: number;
  markFieldModified: (field: string) => void;
  handleFillManually: () => void;
  reindexBuyerAiFields: (removedIndex: number) => void;
  rowAi: BuyerRowAi;
}

/**
 * The AI session behind a buyer form: one scope per buyer row, keyed by `buyerKey`, so a buyer's own
 * ID scan or company documents fill only that row, plus an optional form-wide `sharedScope`.
 */
export const useBuyerFormAi = ({
  form,
  buyerFields,
  trustAccountId,
  isSaving,
  isSaveError,
  onSave,
  onCancel,
  sharedScope
}: UseBuyerFormAiParams): {ai: BuyerFormAi; handleCancel: () => void} => {
  const {rowScopes, resolveRowField} = useBuyerRowAiScopes(form.control, buyerFields);

  const resolveField = (targetField: string, scopeId: string): string | null =>
    sharedScope && scopeId === sharedScope.scope.id
      ? sharedScope.resolveField(targetField)
      : resolveRowField(targetField, scopeId);

  const {
    aiFields,
    processingScopes,
    suggestionsCountByScope,
    markFieldModified,
    handleCancelWithAI,
    stopScope,
    reindexFieldArray
  } = useFormShell<CreateBuyerFormValues>({
    isDirty: form.formState.isDirty,
    isLoading: false,
    isSaving,
    isError: false,
    isSaveError,
    onSave,
    onCancel,
    ai: {
      entityType: DocumentEntityType.TRUST_ACCOUNT,
      entityId: trustAccountId,
      documentContext: sharedScope?.documentContext ?? DocumentContext.KYC,
      documentTypes: sharedScope?.documentTypes ?? NO_SHARED_DOCUMENT_TYPES,
      documentScopes: sharedScope ? [sharedScope.scope, ...rowScopes] : rowScopes,
      getValues: form.getValues,
      setValue: form.setValue,
      resolveField
    }
  });

  const reindexBuyerAiFields = (removedIndex: number) =>
    reindexFieldArray({fieldArrayPrefix: 'buyers', removedIndex});

  const sharedScopeId = sharedScope?.scope.id;

  return {
    ai: {
      aiFields,
      isProcessing: sharedScopeId !== undefined && processingScopes.has(sharedScopeId),
      suggestionsCount:
        sharedScopeId === undefined ? 0 : (suggestionsCountByScope.get(sharedScopeId) ?? 0),
      markFieldModified,
      handleFillManually: () => {
        if (sharedScopeId !== undefined) stopScope(sharedScopeId);
      },
      reindexBuyerAiFields,
      rowAi: {processingScopes, suggestionsCountByScope, stopScope}
    },
    handleCancel: handleCancelWithAI
  };
};

function useBuyerRowAiScopes(
  control: Control<CreateBuyerFormValues>,
  buyerFields: FieldArrayWithId<CreateBuyerFormValues, 'buyers'>[]
) {
  const rowBuyerKeys = buyerFields.map(field => field.person.buyerKey);
  // Only the row's selected types are watched, so a document left over from before a type switch can't prefill it.
  const rowIdTypes = useWatch({
    control,
    name: buyerFields.map((_, i) => `buyers.${i}.person.idType` as const)
  });
  const rowPartyTypes = useWatch({
    control,
    name: buyerFields.map((_, i) => `buyers.${i}.partyType` as const)
  });

  const rowScopes: AiDocumentScope[] = rowBuyerKeys.map((buyerKey, i) => ({
    id: buyerKey,
    metadata: {buyerKey},
    documentContext: DocumentContext.KYC,
    documentTypes: getAcceptedKycDocumentTypes({
      partyType: rowPartyTypes[i] ?? PartyType.PERSON,
      idType: rowIdTypes[i]
    })
  }));

  const resolveRowField = (targetField: string, scopeId: string): string | null =>
    resolveBuyerRowField({targetField, scopeId, rowBuyerKeys, rowPartyTypes});

  return {rowScopes, resolveRowField};
}

interface ResolveBuyerRowFieldParams {
  targetField: string;
  scopeId: string;
  rowBuyerKeys: string[];
  rowPartyTypes: (PartyType | undefined)[];
}

/** Maps a row scope's extracted field onto that row; null once the row is removed or for a non-row scope. */
export function resolveBuyerRowField({
  targetField,
  scopeId,
  rowBuyerKeys,
  rowPartyTypes
}: ResolveBuyerRowFieldParams): string | null {
  const rowIndex = rowBuyerKeys.indexOf(scopeId);
  if (rowIndex === -1) return null;
  const group = rowPartyTypes[rowIndex] === PartyType.ORGANIZATION ? 'organization' : 'person';
  return `buyers.${rowIndex}.${group}.${targetField}`;
}
